import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RiRobot2Line } from 'react-icons/ri';
import { FiX, FiSettings, FiCheckSquare, FiMoon, FiSun } from 'react-icons/fi';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/contexts/ThemeContext';
import { getUserDisplayName } from '@/utils/user';
import Avatar from '@/components/common/Avatar';
import {
  useGetNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  useSearchQuery,
  useGetFollowUpsQuery,
  useGetSubscriptionQuery,
  useGetCurrentTenantQuery,
} from '@/store/api/apiSlice';
import { useKeyboardShortcut } from '@/hooks/useKeyboardShortcut';
import SearchResults from '@/components/SearchResults';
import { useNotificationStream } from '@/hooks/useNotificationStream';
import ProductSwitcher from '@/components/layout/ProductSwitcher';
import OrgContextBadge from '@/components/layout/OrgContextBadge';
import { useMobileNav } from './MobileNavContext';
import { shouldUseOrgConsoleNav } from '@/utils/orgConsole';
import LanguageSwitcher from '@/components/layout/LanguageSwitcher';
import TodayPanel from '@/components/today/TodayPanel';
import TodayPanelFullscreen from '@/components/today/TodayPanelFullscreen';
import { countTodayScope, countOverdueScope } from '@/components/today/followUpUtils';
import { PRODUCT_BY_ID } from '@/config/products';
import { getActiveProductId } from '@/utils/productAccess';

const HeaderIcon = ({ children }: { children: React.ReactNode }) => (
  <svg
    className="w-[16px] h-[16px] shrink-0 stroke-current fill-none"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 18 18"
  >
    {children}
  </svg>
);

type PageMeta = {
  title: string;
  icon: React.ReactNode;
  match: (pathname: string) => boolean;
};

function formatUserRole(role?: string): string {
  if (!role) return 'User';
  return role
    .split('_')
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
}

const PAGE_META: PageMeta[] = [
  {
    title: 'AIDLC Hub',
    match: (pathname) => pathname.startsWith('/workspace'),
    icon: (
      <>
        <rect x="2" y="2" width="6" height="6" rx="1.5" />
        <rect x="10" y="2" width="6" height="6" rx="1.5" />
        <rect x="2" y="10" width="6" height="6" rx="1.5" />
        <rect x="10" y="10" width="6" height="6" rx="1.5" />
      </>
    ),
  },
  {
    title: 'Dashboard',
    match: (pathname) => pathname === '/dashboard',
    icon: (
      <>
        <rect x="2" y="2" width="6" height="6" rx="1.5" />
        <rect x="10" y="2" width="6" height="6" rx="1.5" />
        <rect x="2" y="10" width="6" height="6" rx="1.5" />
        <rect x="10" y="10" width="6" height="6" rx="1.5" />
      </>
    ),
  },
  {
    title: 'AI Studio',
    match: (pathname) => pathname.startsWith('/ai-studio'),
    icon: (
      <>
        <path d="M9 2a7 7 0 1 0 0 14A7 7 0 0 0 9 2z" />
        <path d="M7 9l2 2 2-3" />
      </>
    ),
  },
  {
    title: 'Whiteboard',
    match: (pathname) => pathname.startsWith('/ai-whiteboard'),
    icon: (
      <>
        <rect x="2" y="3" width="14" height="12" rx="1.5" />
        <path d="M5 6h8M5 9h5M5 12h6" />
      </>
    ),
  },
  { title: 'Projects', match: (pathname) => pathname.startsWith('/projects'), icon: <path d="M2 5h14M2 9h14M2 13h8" /> },
  {
    title: 'Board',
    match: (pathname) => pathname.startsWith('/board'),
    icon: (
      <>
        <rect x="2" y="3" width="4" height="12" rx="1.5" />
        <rect x="7" y="3" width="4" height="8" rx="1.5" />
        <rect x="12" y="3" width="4" height="10" rx="1.5" />
      </>
    ),
  },
  {
    title: 'Backlog',
    match: (pathname) => pathname.startsWith('/backlog'),
    icon: (
      <>
        <rect x="2" y="2" width="14" height="3" rx="1" />
        <rect x="2" y="7" width="14" height="3" rx="1" />
        <rect x="2" y="12" width="14" height="3" rx="1" />
      </>
    ),
  },
  {
    title: 'Sprints',
    match: (pathname) => pathname.startsWith('/sprints'),
    icon: (
      <>
        <circle cx="9" cy="9" r="7" />
        <path d="M9 5v4l3 2" />
      </>
    ),
  },
  { title: 'List View', match: (pathname) => pathname.startsWith('/list'), icon: <path d="M2 5h14M2 9h14M2 13h14" /> },
  {
    title: 'Calendar',
    match: (pathname) => pathname.startsWith('/calendar'),
    icon: (
      <>
        <rect x="2" y="3" width="14" height="13" rx="2" />
        <path d="M2 7h14M6 3v4M12 3v4" />
      </>
    ),
  },
  {
    title: 'Gantt Schedule',
    match: (pathname) => pathname.startsWith('/gantt'),
    icon: (
      <>
        <rect x="4" y="3" width="10" height="2.5" rx="1" />
        <rect x="2" y="7.5" width="8" height="2.5" rx="1" />
        <rect x="6" y="12" width="10" height="2.5" rx="1" />
      </>
    ),
  },
  {
    title: 'Workload',
    match: (pathname) => pathname.startsWith('/workload'),
    icon: (
      <>
        <rect x="2" y="12" width="3" height="4" rx="0.5" />
        <rect x="6.5" y="8" width="3" height="8" rx="0.5" />
        <rect x="11" y="4" width="3" height="12" rx="0.5" />
      </>
    ),
  },
  {
    title: 'Time Tracking',
    match: (pathname) => pathname.startsWith('/time-tracking'),
    icon: (
      <>
        <circle cx="9" cy="9" r="7" />
        <path d="M9 5v4l2.5 2.5" />
      </>
    ),
  },
  { title: 'Reports', match: (pathname) => pathname.startsWith('/reports'), icon: <path d="M2 14L5.5 9l3 2.5L14 4" /> },
  {
    title: 'Executive Summary',
    match: (pathname) => pathname.startsWith('/stakeholder'),
    icon: (
      <>
        <circle cx="9" cy="7" r="3" />
        <path d="M3 17c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      </>
    ),
  },
  {
    title: 'Project Economics',
    match: (pathname) => pathname.startsWith('/economics'),
    icon: (
      <>
        <path d="M9 2v14" />
        <path d="M5 6h6a3 3 0 010 6H7a3 3 0 000 6h8" />
      </>
    ),
  },
  {
    title: 'CoTaskr Cost',
    match: (pathname) => pathname.startsWith('/cost'),
    icon: (
      <>
        <rect x="2" y="4" width="14" height="11" rx="2" />
        <path d="M2 8h14M6 12h2" />
      </>
    ),
  },
  {
    title: 'Org Members',
    match: (pathname) => pathname.startsWith('/members') || pathname.startsWith('/teams'),
    icon: (
      <>
        <circle cx="7" cy="7" r="3.5" />
        <circle cx="13" cy="7" r="3.5" />
        <path d="M1 17c0-3 2.2-5 5-5M7 12c2.2 0 4.5 1.5 5 4" />
      </>
    ),
  },
  {
    title: 'Notifications',
    match: (pathname) => pathname.startsWith('/notifications'),
    icon: (
      <>
        <path d="M9 2a5 5 0 0 0-5 5v3l-1.5 2h13L14 10V7a5 5 0 0 0-5-5z" />
        <path d="M7 15a2 2 0 0 0 4 0" />
      </>
    ),
  },
  {
    title: 'Org Management',
    match: (pathname) => pathname.startsWith('/admin'),
    icon: (
      <>
        <rect x="2" y="4" width="14" height="10" rx="1.5" />
        <path d="M9 4V2M5 8h8M5 11h5" />
      </>
    ),
  },
  {
    title: 'SSO Configuration',
    match: (pathname) => pathname.startsWith('/sso-config'),
    icon: (
      <>
        <circle cx="9" cy="9" r="7" />
        <path d="M6 9h6M9 6l-3 3 3 3" />
      </>
    ),
  },
  {
    title: 'Integrations',
    match: (pathname) => pathname.startsWith('/integrations'),
    icon: <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />,
  },
  {
    title: 'Webhooks',
    match: (pathname) => pathname.startsWith('/webhooks'),
    icon: <path d="M3 9h12M3 13h12M7 5l-4 4 4 4M11 5l4 4-4 4" />,
  },
  {
    title: 'Workflows',
    match: (pathname) => pathname.startsWith('/workflows'),
    icon: (
      <>
        <path d="M4 9h5l2-3 2 6 2-3h3" />
        <circle cx="4" cy="9" r="1.5" />
        <circle cx="16" cy="9" r="1.5" />
      </>
    ),
  },
  {
    title: 'Roles & Permissions',
    match: (pathname) => pathname.startsWith('/roles'),
    icon: (
      <>
        <path d="M12 2L2 7v7c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7L12 2z" />
        <path d="M9 12l2 2 4-4" />
      </>
    ),
  },
  {
    title: 'AI Usage',
    match: (pathname) => pathname.startsWith('/ai-usage'),
    icon: (
      <>
        <path d="M2 3h14v12H2V3z" />
        <path d="M2 7h14M6 3v12M10 3v12" />
      </>
    ),
  },
  {
    title: 'AI Assistant',
    match: (pathname) => pathname.startsWith('/ai-assistant'),
    icon: (
      <>
        <circle cx="9" cy="9" r="7" />
        <path d="M6 10h6M7 7.5h.01M11 7.5h.01" />
      </>
    ),
  },
  {
    title: 'Settings',
    match: (pathname) => pathname.startsWith('/settings'),
    icon: (
      <>
        <circle cx="9" cy="9" r="2.5" />
        <path d="M9 2v2M9 14v2M2 9h2M14 9h2M4 4l1.5 1.5M12.5 12.5L14 14M4 14l1.5-1.5M12.5 5.5L14 4" />
      </>
    ),
  },
  {
    title: 'Workspace home',
    match: (pathname) => pathname === '/org' || pathname === '/org/setup',
    icon: (
      <>
        <rect x="2" y="4" width="14" height="10" rx="1.5" />
        <path d="M9 4V2M5 8h8M5 11h5" />
      </>
    ),
  },
  {
    title: 'Products & seats',
    match: (pathname) => pathname.startsWith('/org/products'),
    icon: (
      <>
        <rect x="2" y="2" width="6" height="6" rx="1.5" />
        <rect x="10" y="2" width="6" height="6" rx="1.5" />
        <rect x="2" y="10" width="6" height="6" rx="1.5" />
        <rect x="10" y="10" width="6" height="6" rx="1.5" />
      </>
    ),
  },
  {
    title: 'Licenses & users',
    match: (pathname) => pathname.startsWith('/org/licenses'),
    icon: (
      <>
        <circle cx="7" cy="7" r="3.5" />
        <circle cx="13" cy="7" r="3.5" />
        <path d="M1 17c0-3 2.2-5 5-5M7 12c2.2 0 4.5 1.5 5 4" />
      </>
    ),
  },
  {
    title: 'Org security',
    match: (pathname) => pathname.startsWith('/org/security'),
    icon: (
      <>
        <path d="M12 2L2 7v7c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7L12 2z" />
        <path d="M9 12l2 2 4-4" />
      </>
    ),
  },
  {
    title: 'Billing',
    match: (pathname) => pathname.startsWith('/billing'),
    icon: (
      <>
        <rect x="2" y="4" width="14" height="10" rx="2" />
        <path d="M2 8h14" />
      </>
    ),
  },
];

function Header({ onStartTour }: { onStartTour?: () => void }) {
  const { t } = useTranslation();
  const mobileNav = useMobileNav();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const { isDark, toggleDarkMode } = useTheme();
  const displayName = getUserDisplayName(user);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showFollowUps, setShowFollowUps] = useState(false);
  const [followUpsFullscreen, setFollowUpsFullscreen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchEditable, setSearchEditable] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const followUpRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  const { data: notifications = [], refetch: refetchNotifications } = useGetNotificationsQuery({ limit: 10 });
  const { data: followUps = [], refetch: refetchFollowUps } = useGetFollowUpsQuery(undefined, {
    skip: !isAuthenticated,
    pollingInterval: 60_000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: true,
  });

  const { data: subscription } = useGetSubscriptionQuery(undefined, { skip: !isAuthenticated });
  const tenantSuspended =
    subscription?.tenantStatus === 'SUSPENDED' || subscription?.tenantStatus === 'CANCELLED';

  // Real-time notification push via SSE — refetches the list when a new notification arrives
  useNotificationStream(refetchNotifications, { enabled: !tenantSuspended });
  const [markNotificationRead] = useMarkNotificationReadMutation();
  const [markAllNotificationsRead] = useMarkAllNotificationsReadMutation();

  // Normalize the search query: strip leading '#' so "#42" searches for "42"
  const activeProductShortName =
    PRODUCT_BY_ID[getActiveProductId(location.pathname, location.search)].shortName;
  const orgConsoleMode = shouldUseOrgConsoleNav(user?.role, location.pathname);
  const { data: tenant } = useGetCurrentTenantQuery(undefined, { skip: !orgConsoleMode });
  const tenantSettings = (tenant?.settings ?? {}) as { logoUrl?: string };
  const currentPageMeta = PAGE_META.find((meta) => meta.match(location.pathname));

  const normalizedSearchQuery = searchQuery.trim().startsWith('#')
    ? searchQuery.trim().slice(1)
    : searchQuery.trim();

  // Search query with debounce
  const { data: searchResults, isLoading: searchLoading } = useSearchQuery(
    { q: normalizedSearchQuery, limit: 5 },
    { skip: normalizedSearchQuery.length < 1 }
  );

  const unreadCount = notifications.filter((n: any) => !n.isRead).length;
  const hasMentionNotification = notifications.some((n: any) => !n.isRead && n.type === 'MENTION');
  const followUpBadgeCount = countTodayScope(followUps);
  const followUpOverdueCount = countOverdueScope(followUps);
  const showFollowUpReminder = followUpBadgeCount > 0 && !showFollowUps;
  const followUpTitle = followUpBadgeCount > 0
    ? t('header.followUpsToday', { count: followUpBadgeCount })
    : t('header.followUps');

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  const handleMarkRead = async (id: string) => {
    await markNotificationRead(id).unwrap().catch(() => null);
    refetchNotifications();
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead([]).unwrap().catch(() => null);
    refetchNotifications();
  };

  // Cmd+K on macOS
  useKeyboardShortcut(
    { key: 'k', metaKey: true },
    () => {
      searchInputRef.current?.focus();
      setShowSearchResults(true);
    }
  );

  // Ctrl+K on Windows/Linux
  useKeyboardShortcut(
    { key: 'k', ctrlKey: true },
    () => {
      searchInputRef.current?.focus();
      setShowSearchResults(true);
    }
  );

  // Escape closes search dropdown
  useKeyboardShortcut(
    { key: 'Escape' },
    () => {
      setShowSearchResults(false);
      setSearchQuery('');
      searchInputRef.current?.blur();
    }
  );

  useEffect(() => {
    const handler = () => {
      setSearchQuery('');
      setShowSearchResults(false);
      searchInputRef.current?.blur();
    };
    window.addEventListener('cotaskr:clear-global-search', handler);
    return () => window.removeEventListener('cotaskr:clear-global-search', handler);
  }, []);

  useEffect(() => {
    const openFollowUps = () => {
      setShowFollowUps(true);
      setShowNotifications(false);
      refetchFollowUps();
    };
    window.addEventListener('cotaskr:open-follow-ups', openFollowUps);
    return () => window.removeEventListener('cotaskr:open-follow-ups', openFollowUps);
  }, [refetchFollowUps]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
      if (followUpRef.current && !followUpRef.current.contains(e.target as Node)) {
        setShowFollowUps(false);
      }
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <header className="relative z-[50] h-[52px] bg-topbar border-b border-border flex items-center px-5 gap-2 shrink-0">
      <button
        type="button"
        className="lg:hidden relative z-[60] w-9 h-9 flex items-center justify-center rounded-[var(--radius-sm)] text-text-secondary hover:bg-surface-hover shrink-0"
        aria-label={t('header.openNavMenu')}
        aria-expanded={false}
        data-testid="mobile-nav-open"
        onClick={() => mobileNav?.open()}
      >
        <svg className="w-5 h-5 stroke-current fill-none" strokeWidth={2} strokeLinecap="round" viewBox="0 0 24 24">
          <path d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      <ProductSwitcher />
      {orgConsoleMode && (
        <OrgContextBadge
          orgName={tenant?.name ?? user?.tenant?.name}
          logoUrl={tenantSettings.logoUrl}
        />
      )}
      {orgConsoleMode && currentPageMeta && (
        <div
          className="hidden md:flex items-center gap-2 shrink-0 pe-1 border-r border-border me-1"
          aria-label={currentPageMeta.title}
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-surface-hover text-text-secondary">
            <HeaderIcon>{currentPageMeta.icon}</HeaderIcon>
          </span>
          <span className="text-[13px] font-semibold text-text-primary whitespace-nowrap">
            {currentPageMeta.title}
          </span>
        </div>
      )}
      {/* Search */}
      <div ref={searchRef} className="relative max-w-[720px] flex-1 min-w-0" data-tour="header-search">
        <div className="header-search-bar flex w-full items-stretch overflow-hidden rounded-[var(--radius-md)] border border-border bg-[var(--topbar-input-bg)] transition-all focus-within:border-border-focus focus-within:shadow-focus">
          <div className="relative min-w-0 flex-1 flex items-center">
            <svg
              className="pointer-events-none absolute start-2.5 top-1/2 z-[1] -translate-y-1/2 w-[14px] h-[14px] stroke-current text-text-muted fill-none"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 18 18"
              aria-hidden
            >
              <circle cx="7.5" cy="7.5" r="5" />
              <path d="M12 12l4 4" />
            </svg>
            <input
              ref={searchInputRef}
              type="search"
              name="global-header-search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSearchResults(true);
              }}
              onFocus={() => {
                setSearchEditable(true);
                setShowSearchResults(true);
              }}
              readOnly={!searchEditable}
              placeholder={t('header.searchPlaceholder', { product: activeProductShortName })}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
              data-global-search
              role="searchbox"
              aria-label={`Search in ${activeProductShortName}`}
              className="w-full border-0 bg-transparent h-7 py-0 ps-8 pe-10 text-[12px] text-text-primary placeholder:text-text-muted focus:outline-none"
            />
            <span className="pointer-events-none absolute end-2.5 top-1/2 -translate-y-1/2 text-[10px] font-mono rounded border border-border px-1 py-px text-text-muted bg-[var(--surface)]/80">
              ⌘K
            </span>
          </div>
        </div>

        {/* Search Results */}
        {showSearchResults && normalizedSearchQuery.length >= 1 && (
          <SearchResults
            query={searchQuery}
            results={{
              tasks: searchResults?.tasks || [],
              projects: searchResults?.projects || [],
              users: searchResults?.users || [],
            }}
            isLoading={searchLoading}
            onClose={() => {
              setShowSearchResults(false);
              setSearchQuery('');
            }}
            onNavigate={() => {
              setShowSearchResults(false);
              setSearchQuery('');
            }}
          />
        )}
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Actions */}
      <div className="flex items-center gap-1">
        {/* Dark mode */}
        <button
          type="button"
          onClick={toggleDarkMode}
          className="relative w-8 h-8 flex items-center justify-center rounded-[var(--radius-sm)] text-text-secondary hover:bg-surface-hover transition-colors"
          title={isDark ? t('header.lightMode') : t('header.darkMode')}
          aria-label={isDark ? t('header.lightMode') : t('header.darkMode')}
        >
          {isDark ? <FiSun className="w-[16px] h-[16px]" /> : <FiMoon className="w-[16px] h-[16px]" />}
        </button>

        {/* AI Assistant */}
        <button
          onClick={() => navigate('/ai-assistant')}
          data-tour="header-ai-assistant"
          className="group relative w-8 h-8 flex items-center justify-center rounded-full text-white bg-brand shadow-md hover:bg-brand-dark hover:scale-105 transition-all duration-300"
          title={t('header.aiAssistant')}
          aria-label={t('header.openAiAssistant')}
        >
          <span className="pointer-events-none absolute inset-0 rounded-full blur-md animate-pulse" style={{ backgroundColor: 'var(--brand)', opacity: 0.4 }} />
          <span className="absolute -top-2 -right-2 rounded-full bg-white border px-1.5 py-[1px] text-[8px] font-extrabold leading-none shadow-sm animate-bounce" style={{ color: 'var(--brand)', borderColor: 'var(--brand)' }}>
            AI
          </span>
          <RiRobot2Line className="relative z-10 w-[18px] h-[18px] transition-transform duration-300 group-hover:scale-110" />
        </button>

        {/* Follow-ups */}
        <div className="relative" ref={followUpRef} data-tour="header-follow-ups">
          <button
            type="button"
            onClick={() => {
              const opening = !showFollowUps;
              setShowFollowUps(opening);
              if (opening) {
                setShowNotifications(false);
                refetchFollowUps();
              }
            }}
            className={`relative flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] transition-colors ${
              showFollowUpReminder
                ? followUpOverdueCount > 0
                  ? 'text-red-600 hover:bg-red-50 animate-follow-up-nudge'
                  : 'text-amber-600 hover:bg-amber-50 animate-follow-up-nudge'
                : 'text-text-secondary hover:bg-surface-hover'
            }`}
            title={followUpTitle}
            aria-label={followUpTitle}
            aria-expanded={showFollowUps}
          >
            {showFollowUpReminder && (
              <span
                className={`pointer-events-none absolute inset-0 rounded-[var(--radius-sm)] animate-follow-up-glow ${
                  followUpOverdueCount > 0 ? 'bg-red-400/30' : 'bg-amber-400/35'
                }`}
              />
            )}
            <FiCheckSquare className={`relative z-10 h-[17px] w-[17px] ${showFollowUpReminder ? 'drop-shadow-sm' : ''}`} />
            {followUpBadgeCount > 0 && (
              <>
                {showFollowUpReminder && (
                  <span
                    className={`pointer-events-none absolute top-1 end-1 h-2 w-2 rounded-full animate-ping ${
                      followUpOverdueCount > 0 ? 'bg-red-500' : 'bg-amber-500'
                    }`}
                  />
                )}
                <span
                  className={`absolute -top-0.5 -end-0.5 z-10 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-bold text-white ${
                    followUpOverdueCount > 0 ? 'bg-red-600' : 'bg-brand'
                  }`}
                >
                  {followUpBadgeCount}
                </span>
              </>
            )}
          </button>

          {showFollowUps && (
            <div className="absolute end-0 mt-2 z-[100]">
              <TodayPanel
                onExpand={() => {
                  setShowFollowUps(false);
                  setFollowUpsFullscreen(true);
                }}
                onClose={() => setShowFollowUps(false)}
              />
            </div>
          )}
        </div>

        {followUpsFullscreen && (
          <TodayPanelFullscreen onClose={() => setFollowUpsFullscreen(false)} />
        )}

        {/* Notifications */}
        <div className="relative" ref={notifRef} data-tour="header-notifications">
          <button
            onClick={() => {
              const opening = !showNotifications;
              setShowNotifications(opening);
              if (opening) {
                setShowFollowUps(false);
                refetchNotifications();
              }
            }}
            className={`relative w-8 h-8 flex items-center justify-center rounded-[var(--radius-sm)] text-text-secondary hover:bg-surface-hover transition-colors ${hasMentionNotification ? 'animate-[wiggle_0.5s_ease-in-out_3]' : ''}`}
          >
            <svg
              className={`w-[17px] h-[17px] stroke-current fill-none ${hasMentionNotification ? 'animate-swing' : ''}`}
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 18 18"
            >
              <path d="M9 2a5.5 5.5 0 0 1 5.5 5.5v3.5l1.5 2H2l1.5-2V7.5A5.5 5.5 0 0 1 9 2z" />
              <path d="M7.5 16a1.5 1.5 0 0 0 3 0" />
            </svg>
            {unreadCount > 0 && (
              <span
                className={`absolute top-1.5 end-1.5 h-2 w-2 rounded-full ${
                  hasMentionNotification ? 'bg-brand animate-ping' : 'bg-brand'
                }`}
              />
            )}
          </button>

          {showNotifications && (
            <div className="header-popup absolute end-0 mt-2 w-80 bg-surface rounded-lg shadow-lg border border-border z-[100] overflow-hidden">
              <div className="px-4 py-2 border-b border-border flex items-center justify-between">
                <h3 className="text-[13.5px] font-bold text-text-primary">{t('header.notifications')}</h3>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-[12px] text-brand font-medium hover:underline"
                  >
                    {t('header.markAllRead')}
                  </button>
                )}
              </div>
              <div className="max-h-96 overflow-y-auto scrollbar-thin">
                {notifications.length === 0 ? (
                  <p className="px-4 py-6 text-[13px] text-text-muted text-center">{t('header.noNotifications')}</p>
                ) : (
                  notifications.map((item: any) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        handleMarkRead(item.id);
                        if (item.link) navigate(item.link);
                        setShowNotifications(false);
                      }}
                      className={`w-full text-start px-4 py-3 hover:bg-surface-hover cursor-pointer transition-colors ${!item.isRead ? (item.type === 'MENTION' ? 'bg-indigo-50 border-s-2 border-indigo-400' : 'bg-brand-light') : ''}`}
                    >
                      <p className="text-[13px] font-medium text-text-primary flex items-center gap-1.5">
                        {item.type === 'MENTION' && <span className="text-indigo-500 text-[11px] font-bold">@</span>}
                        {item.title}
                      </p>
                      <p className="text-[12px] text-text-secondary mt-0.5">{item.message}</p>
                      <p className="text-[11px] text-text-muted mt-1">
                        {new Date(item.createdAt).toLocaleString()}
                      </p>
                    </button>
                  ))
                )}
              </div>
              <div className="border-t border-border px-3 py-2 bg-surface">
                <button
                  type="button"
                  className="w-full rounded-md py-2 text-[12px] font-semibold text-brand hover:bg-brand-light transition-colors"
                  onClick={() => {
                    navigate('/notifications');
                    setShowNotifications(false);
                  }}
                >
                  {t('header.openNotificationCenter')}
                </button>
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => navigate('/settings')}
          data-tour="header-settings"
          title={t('header.openSettings')}
          aria-label={t('header.openSettings')}
          className="w-8 h-8 flex items-center justify-center rounded-[var(--radius-sm)] text-text-secondary hover:bg-surface-hover transition-colors"
        >
          <FiSettings className="w-[17px] h-[17px]" />
        </button>

        <LanguageSwitcher />

      </div>

      {/* User Avatar */}
      <div className="relative" ref={userRef}>
        <button
          onClick={() => setShowUserMenu(!showUserMenu)}
          title={displayName}
          className="w-8 h-8 rounded-full hover:ring-2 hover:ring-brand/40 transition-all shrink-0 overflow-hidden"
        >
          <Avatar name={displayName} variant="brand" size="md" className="!w-8 !h-8" />
        </button>

        {showUserMenu && (
          <div className="header-popup absolute end-0 mt-2 w-56 bg-surface rounded-lg shadow-lg border border-border py-1 z-[100]">
            <div className="px-4 py-2.5 border-b border-border">
              <p className="text-[13px] font-semibold text-text-primary">{displayName}</p>
              <p className="text-[12px] text-text-muted">{user?.email}</p>
              {user?.role && (
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                  {formatUserRole(user.role)}
                </p>
              )}
            </div>
            <button
              type="button"
              data-tour="header-settings"
              className="w-full px-4 py-2 text-left text-[13px] text-text-secondary hover:bg-surface-hover flex items-center gap-3 transition-colors"
              onClick={() => {
                setShowUserMenu(false);
                navigate('/settings');
              }}
            >
              {t('header.profileSettings')}
            </button>
            <div className="border-t border-border my-1" />
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 text-left text-[13px] text-blocked hover:bg-blocked-bg/50 flex items-center gap-3 transition-colors"
            >
              {t('common.signOut')}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
